import { useState } from 'react';
import { useForgeNavigate } from '../components/layout/ForgeTransition';
import { PageWrapper } from '../components/layout/PageWrapper';
import { Navbar } from '../components/layout/Navbar';
import { ProgressTracker } from '../components/layout/ProgressTracker';
import { Card } from '../components/shared/Card';
import { Button } from '../components/shared/Button';
import { Badge } from '../components/shared/Badge';
import { Switch } from '../components/shared/Switch';
import { useAppState } from '../hooks/useAppState';
import { getFirmsByMarket, isFirmSMOGEligible } from '../data/firms';
import { formatCurrency } from '../utils/formatting';
import { Lock, DollarSign, RotateCw } from 'lucide-react';
import type { PropFirm, PropFirmPlan, PropFirmAccount } from '../types';

function formatSize(size: number) {
  return size >= 1000 ? `$${(size / 1000).toFixed(0)}K` : `$${size}`;
}

export function FirmSelection() {
  const forgeNavigate = useForgeNavigate();
  const { user, updateUser } = useAppState();

  const [selectedFirmId, setSelectedFirmId] = useState<string | null>(null);
  const [market, setMarket] = useState<'futures' | 'crypto'>('futures');
  const [selectedPlanIds, setSelectedPlanIds] = useState<Record<string, string>>({});
  const [selectedSizes, setSelectedSizes] = useState<Record<string, number>>({});
  const [flippedCards, setFlippedCards] = useState<Record<string, boolean>>({});

  const isSMOG = user?.currentStrategy === 'SMOG';
  const visibleFirms = getFirmsByMarket(market);

  function getActivePlan(firm: PropFirm): PropFirmPlan {
    const planId = selectedPlanIds[firm.id];
    if (planId) {
      const found = firm.plans.find(p => p.id === planId);
      if (found) return found;
    }
    return firm.plans[0];
  }

  function getSelectedAccount(plan: PropFirmPlan): PropFirmAccount {
    const selectedSize = selectedSizes[plan.id];
    if (selectedSize) {
      const found = plan.accounts.find(a => a.size === selectedSize);
      if (found) return found;
    }
    return plan.accounts[0];
  }

  function handlePlanSelect(firmId: string, planId: string) {
    setSelectedPlanIds(prev => ({ ...prev, [firmId]: planId }));
    // Reset flip state when switching plans
    setFlippedCards(prev => ({ ...prev, [firmId]: false }));
  }

  function handleSizeSelect(firmId: string, planId: string, size: number) {
    setSelectedSizes(prev => ({ ...prev, [planId]: size }));
    const firm = visibleFirms.find(f => f.id === firmId);
    const disabled = isSMOG && firm && !isFirmSMOGEligible(firm);
    if (!disabled) setSelectedFirmId(firmId);
  }

  function handleFlip(firmId: string) {
    setFlippedCards(prev => ({ ...prev, [firmId]: !prev[firmId] }));
  }

  function handleMarketSwitch(newMarket: 'futures' | 'crypto') {
    setMarket(newMarket);
    setSelectedFirmId(null);
    setSelectedPlanIds({});
    setSelectedSizes({});
    setFlippedCards({});
  }

  function handleContinue() {
    if (!selectedFirmId) return;
    const firm = visibleFirms.find(f => f.id === selectedFirmId);
    if (firm) {
      const plan = getActivePlan(firm);
      const account = getSelectedAccount(plan);
      updateUser({ currentFirmId: selectedFirmId, currentPlanId: plan.id, accountSize: account.size });
    }
    forgeNavigate('/prescription', 5);
  }

  return (
    <PageWrapper warmth={0.55}>
      <Navbar />
      <main className="min-h-screen pt-16">
        <ProgressTracker currentStep={5} />

        <div className="max-w-[700px] mx-auto px-8 pb-16">
          <h1 className="text-2xl md:text-3xl font-bold font-mono tracking-wide text-shimmer text-center mb-3">Select Your Firm</h1>
          <p className="text-[13px] font-mono text-white/50 text-center mb-8 tracking-wide">
            Choose your target prop firm. Your protocol will include firm-specific rules.
          </p>

          {/* Market Toggle */}
          <div className="flex justify-center mb-8">
            <Switch size="medium" className="w-full max-w-[320px]" aria-label="Market selection">
              <Switch.Control
                label="Futures"
                value="futures"
                defaultChecked
                onChange={() => handleMarketSwitch('futures')}
              />
              <Switch.Control
                label="Crypto"
                value="crypto"
                onChange={() => handleMarketSwitch('crypto')}
              />
            </Switch>
          </div>

          {/* Firm Cards */}
          <div className="flex flex-col gap-4">
            {visibleFirms.map((firm, idx) => {
              const disabled = isSMOG && !isFirmSMOGEligible(firm);
              const plan = getActivePlan(firm);
              const account = getSelectedAccount(plan);
              const activeSize = account.size;
              const isFlipped = flippedCards[firm.id] ?? false;
              const isDirectFunded = !plan.hasEval;

              // For direct-funded plans, always show funded view (no flip)
              const showFunded = isDirectFunded || isFlipped;

              return (
                <FirmCard
                  key={firm.id}
                  firm={firm}
                  plan={plan}
                  account={account}
                  activeSize={activeSize}
                  isFlipped={isFlipped}
                  isDirectFunded={isDirectFunded}
                  showFunded={showFunded}
                  disabled={disabled}
                  selected={selectedFirmId === firm.id}
                  isSMOG={isSMOG}
                  idx={idx}
                  onSelectFirm={() => !disabled && setSelectedFirmId(firm.id)}
                  onPlanSelect={(planId) => handlePlanSelect(firm.id, planId)}
                  onSizeSelect={(size) => handleSizeSelect(firm.id, plan.id, size)}
                  onFlip={() => handleFlip(firm.id)}
                  selectedPlanId={selectedPlanIds[firm.id]}
                />
              );
            })}
          </div>

          <div className="mt-8 flex justify-center">
            <Button onClick={handleContinue} disabled={!selectedFirmId}>
              Generate Protocol
            </Button>
          </div>
        </div>
      </main>
    </PageWrapper>
  );
}

/* ─── Firm Card with 3D Flip ───────────────────────────────────────────────── */

interface FirmCardProps {
  firm: PropFirm;
  plan: PropFirmPlan;
  account: PropFirmAccount;
  activeSize: number;
  isFlipped: boolean;
  isDirectFunded: boolean;
  showFunded: boolean;
  disabled: boolean;
  selected: boolean;
  isSMOG: boolean;
  idx: number;
  onSelectFirm: () => void;
  onPlanSelect: (planId: string) => void;
  onSizeSelect: (size: number) => void;
  onFlip: () => void;
  selectedPlanId?: string;
}

function FirmCard({
  firm,
  plan,
  account,
  activeSize,
  isFlipped,
  isDirectFunded,
  showFunded,
  disabled,
  selected,
  isSMOG,
  idx,
  onSelectFirm,
  onPlanSelect,
  onSizeSelect,
  onFlip,
  selectedPlanId,
}: FirmCardProps) {
  const hasMultiplePlans = firm.plans.length > 1;

  // Shared header/tabs/size selector between front and back
  const sharedHeader = (
    <>
      {/* Firm Name + SMOG lock */}
      <div className="flex items-center gap-2 mb-2">
        <h3 className="text-base font-semibold font-mono">{firm.name}</h3>
        {disabled && <Lock className="w-3 h-3 text-[#E84040]" />}
      </div>

      {/* Plan Tabs */}
      {hasMultiplePlans && (
        <div className="flex flex-wrap gap-1.5 mb-3">
          {firm.plans.map(p => {
            const isActive = p.id === plan.id;
            return (
              <button
                key={p.id}
                onClick={(e) => {
                  e.stopPropagation();
                  onPlanSelect(p.id);
                }}
                className={`px-3 py-1.5 rounded-full text-[11px] font-mono font-medium tracking-wide transition-all duration-200 ${
                  isActive
                    ? 'bg-[rgba(60,200,210,0.15)] text-[rgba(100,230,230,0.95)] border border-[rgba(60,200,210,0.4)] shadow-[0_0_10px_rgba(60,200,210,0.12)]'
                    : 'bg-white/[0.06] text-white/45 border border-white/[0.10] hover:bg-white/[0.09] hover:text-white/65'
                }`}
              >
                {p.name}
              </button>
            );
          })}
        </div>
      )}

      {/* Account Size Selector */}
      <div className="flex flex-wrap gap-1.5 mb-4">
        {plan.accounts.map(a => (
          <button
            key={a.size}
            onClick={(e) => {
              e.stopPropagation();
              onSizeSelect(a.size);
            }}
            className={`px-3 py-1 rounded text-[11px] font-mono font-medium transition-all duration-200 ${
              activeSize === a.size
                ? 'bg-[rgba(60,200,210,0.15)] text-[rgba(100,230,230,0.95)] border border-[rgba(60,200,210,0.4)] shadow-[0_0_10px_rgba(60,200,210,0.12)]'
                : 'bg-white/[0.06] text-white/50 border border-white/[0.12] hover:bg-white/[0.09] hover:text-white/70'
            }`}
          >
            {formatSize(a.size)}
          </button>
        ))}
      </div>
    </>
  );

  // ─── EVAL FACE (Front) ──────────────────────────────────────────────────

  const evalConsistency = plan.consistencyRulePct;
  const drawdownType = plan.drawdownType;

  const evalFace = (
    <div className="flex flex-col h-full">
      {sharedHeader}

      {/* Badges */}
      <div className="flex flex-wrap gap-2 mb-3">
        {evalConsistency !== null ? (
          <Badge
            label={`${(evalConsistency * 100).toFixed(0)}% consistency`}
            color={isFirmSMOGEligible(firm) ? '#3DBF7A' : '#E8943A'}
          />
        ) : (
          <Badge label="No consistency rule" color="#3DBF7A" />
        )}
        <Badge label={`${drawdownType} drawdown`} color="#9AB0C0" />
        {firm.maxEvalAccounts !== null && (
          <Badge label={`${firm.maxEvalAccounts} max eval accts`} color="#9AB0C0" />
        )}
      </div>

      {/* Rules Grid */}
      <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-sm font-mono text-white/60">
        <span>Profit Target:</span>
        <span className="text-white/95">{formatCurrency(account.profitTarget)}</span>

        <span>Daily Loss Limit:</span>
        <span className="text-white/95">
          {account.dailyLossLimit !== null && account.dailyLossLimit !== undefined
            ? formatCurrency(account.dailyLossLimit)
            : 'None'}
        </span>

        <span>Max Drawdown:</span>
        <span className="text-white/95">{formatCurrency(account.maxDrawdown)} ({drawdownType})</span>

        {account.maxContracts !== undefined && (
          <>
            <span>Max Contracts:</span>
            <span className="text-white/95">{account.maxContracts}</span>
          </>
        )}
      </div>

      {/* SMOG warning */}
      {disabled && (
        <p className="text-[11px] text-[#E84040] mt-2">
          Not SMOG eligible — consistency rule below 50%
        </p>
      )}

      {/* Flip button */}
      <div className="flex justify-end mt-auto pt-3">
        <button
          onClick={(e) => { e.stopPropagation(); onFlip(); }}
          className="flex items-center gap-1.5 text-[11px] font-mono text-white/40 hover:text-[rgba(100,230,230,0.8)] transition-colors"
        >
          See Funded Rules
          <RotateCw className="w-3 h-3" />
        </button>
      </div>
    </div>
  );

  // ─── FUNDED FACE (Back) ─────────────────────────────────────────────────

  const fundedConsistency = plan.fundedConsistencyRulePct;
  const fundedDDType = plan.fundedDrawdownType ?? plan.drawdownType;
  const fundedDLL = account.fundedDailyLossLimit !== undefined ? account.fundedDailyLossLimit : account.dailyLossLimit;
  const fundedMaxDD = account.fundedMaxDrawdown !== undefined ? account.fundedMaxDrawdown : account.maxDrawdown;
  const fundedContracts = account.fundedMaxContracts !== undefined ? account.fundedMaxContracts : account.maxContracts;

  const fundedFace = (
    <div className="flex flex-col h-full">
      {sharedHeader}

      {/* Badges */}
      <div className="flex flex-wrap gap-2 mb-3">
        {fundedConsistency !== null ? (
          <Badge
            label={`${(fundedConsistency * 100).toFixed(0)}% funded consistency`}
            color={isFirmSMOGEligible(firm) ? '#3DBF7A' : '#E8943A'}
          />
        ) : (
          <Badge label="No funded consistency" color="#3DBF7A" />
        )}
        <Badge label={`${(plan.payoutSplit * 100).toFixed(0)}% payout`} color="#9AB0C0" />
        <Badge label={plan.payoutFrequency} color="#9AB0C0" />
        {firm.maxFundedAccounts !== null && (
          <Badge label={`${firm.maxFundedAccounts} max funded accts`} color="#9AB0C0" />
        )}
      </div>

      {/* Rules Grid */}
      <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-sm font-mono text-white/60">
        <span>Max Drawdown:</span>
        <span className="text-white/95">{formatCurrency(fundedMaxDD)} ({fundedDDType})</span>

        <span>Daily Loss Limit:</span>
        <span className="text-white/95">
          {fundedDLL !== null && fundedDLL !== undefined
            ? formatCurrency(fundedDLL)
            : 'None'}
        </span>

        {fundedContracts !== undefined && (
          <>
            <span>Max Contracts:</span>
            <span className="text-white/95">{fundedContracts}</span>
          </>
        )}

        <span className="flex items-center gap-1">
          <DollarSign className="w-3 h-3 text-white/50" />
          Activation Fee:
        </span>
        <span className={account.activationFee === 0 ? 'text-[#3DBF7A]' : 'text-white/70'}>
          {account.activationFee === 0 ? 'FREE' : formatCurrency(account.activationFee)}
        </span>
      </div>

      {/* SMOG warning */}
      {disabled && (
        <p className="text-[11px] text-[#E84040] mt-2">
          Not SMOG eligible — consistency rule below 50%
        </p>
      )}

      {/* Flip back button (only if plan has eval) */}
      {!isDirectFunded && (
        <div className="flex justify-start mt-auto pt-3">
          <button
            onClick={(e) => { e.stopPropagation(); onFlip(); }}
            className="flex items-center gap-1.5 text-[11px] font-mono text-white/40 hover:text-[rgba(100,230,230,0.8)] transition-colors"
          >
            <RotateCw className="w-3 h-3" />
            See Eval Rules
          </button>
        </div>
      )}
    </div>
  );

  // ─── Direct-funded plans: no flip, just show funded face ────────────────

  if (isDirectFunded) {
    return (
      <Card
        selected={selected}
        disabled={disabled}
        onClick={onSelectFirm}
        style={{
          animation: 'card-enter 0.5s cubic-bezier(0.16,1,0.3,1) both',
          animationDelay: `${idx * 80}ms`,
        }}
      >
        {fundedFace}
      </Card>
    );
  }

  // ─── Flip Card ──────────────────────────────────────────────────────────

  return (
    <div
      style={{
        perspective: '1200px',
        animation: 'card-enter 0.5s cubic-bezier(0.16,1,0.3,1) both',
        animationDelay: `${idx * 80}ms`,
      }}
    >
      <div
        style={{
          position: 'relative',
          transition: 'transform 0.6s cubic-bezier(0.16, 1, 0.3, 1)',
          transformStyle: 'preserve-3d',
          transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
        }}
      >
        {/* Front face — Eval */}
        <div
          style={{
            backfaceVisibility: 'hidden',
            WebkitBackfaceVisibility: 'hidden',
          }}
        >
          <Card
            selected={selected}
            disabled={disabled}
            onClick={onSelectFirm}
          >
            {evalFace}
          </Card>
        </div>

        {/* Back face — Funded */}
        <div
          style={{
            backfaceVisibility: 'hidden',
            WebkitBackfaceVisibility: 'hidden',
            transform: 'rotateY(180deg)',
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
          }}
        >
          <Card
            selected={selected}
            disabled={disabled}
            onClick={onSelectFirm}
          >
            {fundedFace}
          </Card>
        </div>
      </div>
    </div>
  );
}
